// Plain-text helpers for separating human-written content from quoted history and signatures.

export interface SplitBody {
  visible: string;
  quoted: string | null;
  signature: string | null;
}

const QUOTE_HEADER_PATTERNS: RegExp[] = [
  /^On .{4,200}(wrote|écrit|schrieb|escribió|scritto):?\s*$/im,
  /^Le .{4,200}a écrit\s?:?\s*$/im,
  /^Am .{4,200}schrieb .*:?\s*$/im,
  /^-{2,}\s*Original Message\s*-{2,}/im,
  /^-{2,}\s*Forwarded message\s*-{2,}/im,
  /^_{10,}\s*$/m,
  /^From:\s.+\n(Sent|Date):\s.+/im,
  /^De\s?:\s.+\n(Envoyé|Date)\s?:\s.+/im,
];

const SIGNATURE_PATTERNS: RegExp[] = [
  /^-- ?$/m, // RFC 3676 signature delimiter
  /^Sent from my (iPhone|iPad|Android|Galaxy|mobile device|Samsung).*$/im,
  /^Get Outlook for (iOS|Android).*$/im,
  /^Envoyé de mon (iPhone|iPad).*$/im,
];

/** Split a plain-text body into the latest human-written part, quoted history and signature. */
export function splitBody(text: string): SplitBody {
  const normalised = text.replace(/\r\n/g, '\n');
  let cut = normalised.length;
  for (const re of QUOTE_HEADER_PATTERNS) {
    const m = re.exec(normalised);
    if (m && m.index < cut && m.index > 0) cut = m.index;
  }
  // Leading-">" quoted block
  const gt = /^(>.*\n?){2,}/m.exec(normalised);
  if (gt && gt.index < cut && gt.index > 0) cut = gt.index;

  let head = normalised.slice(0, cut);
  const quoted = cut < normalised.length ? normalised.slice(cut).trim() : null;

  let signature: string | null = null;
  let sigCut = head.length;
  for (const re of SIGNATURE_PATTERNS) {
    const m = re.exec(head);
    if (m && m.index < sigCut && m.index > 0) sigCut = m.index;
  }
  if (sigCut < head.length) {
    signature = head.slice(sigCut).replace(/^-- ?\n?/, '').trim() || null;
    head = head.slice(0, sigCut);
  }
  return { visible: head.trim(), quoted: quoted || null, signature };
}

export function normaliseSubject(s: string): string {
  return s.replace(/^\s*((re|fw|fwd|aw|wg|sv|vs|tr|rif|r)\s*(\[\d+\])?\s*:\s*)+/i, '').trim();
}

export function replySubject(s: string): string {
  const base = normaliseSubject(s);
  return /^re:/i.test(s.trim()) ? s.trim() : `Re: ${base}`;
}

export function excerpt(s: string, n = 160): string {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > n ? t.slice(0, n - 1) + '…' : t;
}

/** Extract message-ids from a References / In-Reply-To header value. */
export function parseMessageIds(v: string | null | undefined): string[] {
  if (!v) return [];
  return Array.from(v.matchAll(/<[^<>\s]+>/g)).map((m) => m[0]);
}

const REDACTORS: Record<string, RegExp> = {
  card_numbers: /\b(?:\d[ -]?){13,19}\b/g,
  iban: /\b[A-Z]{2}\d{2}[A-Z0-9 ]{11,30}\b/g,
  phone_numbers: /(?<!\w)\+?\d[\d ()-]{7,}\d(?!\w)/g,
  national_ids: /\b\d{3}-\d{2}-\d{4}\b/g,
  passwords: /(password|passcode|pwd|pin)\s*[:=]\s*\S+/gi,
};

/** Redact sensitive patterns before content leaves for an AI provider. */
export function redact(text: string, kinds: string[]): string {
  let out = text;
  for (const k of kinds) {
    const re = REDACTORS[k];
    if (re) out = out.replace(re, `[redacted ${k.replace('_', ' ')}]`);
  }
  return out;
}

/** Line-based appending of a signature that avoids duplicates when the signature is already present. */
export function applySignature(body: string, signature: string | null): string {
  if (!signature || !signature.trim()) return body;
  const sig = signature.trim();
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
  if (norm(body).includes(norm(sig))) return body;
  return `${body.replace(/\s+$/, '')}\n\n-- \n${sig}`;
}
